import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent } from "@/shadcn/components/ui/sheet";
import Checkbox from "@/components/common/Checkbox";
import AnalyticsService from "@/services/analytics";
import {
  useNearByFilters,
  useI18N,
} from "common/hooks";
import { IoArrowBackOutline } from "react-icons/io5";
import { bucketUrlPrefix } from "common/config";
import { useDarkMode } from "@/hooks/useDarkMode";
import ReactSmoothCollapse from "react-smooth-collapse";

type NearbyFiltersSheetProps = {
  open: boolean;
  onClose: () => void;
  nearbyFilterAccessibility: any[];
  accessibilityFeaturesRequirementsHiddenIds: any[];
  accessibilityFeaturesRequirementsShowHiddenById: string;
};

function NearbyFiltersSheet({ open, onClose, nearbyFilterAccessibility, accessibilityFeaturesRequirementsHiddenIds, accessibilityFeaturesRequirementsShowHiddenById }: NearbyFiltersSheetProps) {
  const i18n = useI18N();
  const isDark = useDarkMode();

  const { filtersState, setFiltersState, } = useNearByFilters();
  const [statusMessage, setStatusMessage] = useState('');

  const [localBusinessTypes, setLocalBusinessTypes] = useState<string[]>();
  const [localAccessibilityOptions, setLocalAccessibilityOptions] = useState<number[]>([]);
  const [openCategories, setOpenCategories] = useState<Record<number, boolean>>({});
  const [showAllTags, setShowAllTags] = useState<Record<number, boolean>>({});
  const [allSubItemsSelected, setAllSubItemsSelected] = useState(false);
  const parentIdRef = useRef<number>(Number(accessibilityFeaturesRequirementsShowHiddenById));

  useEffect(() => {
    setLocalBusinessTypes(filtersState.selectedBusinessTypes);
  }, [filtersState.selectedBusinessTypes]);

  useEffect(() => {
    setLocalAccessibilityOptions(filtersState.accessibilityOptions);
  }, [filtersState.accessibilityOptions]);

  useEffect(() => {
    if (open) {
      AnalyticsService.trackGAEventHandler("nearbyVenuesListOpenFilters");
      AnalyticsService.trackMx("nearbyVenuesListOpenFilters", {});
    }
  }, [open]);

  // Single comprehensive effect to manage sub-item selection state
  useEffect(() => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map(id => Number(id));
    
    // Check if parent is selected
    const hasParent = localAccessibilityOptions.includes(parentId);
    
    // Check if all sub-options are selected
    const allSelected = hasParent && subOptionIds.every(id => 
      localAccessibilityOptions.includes(id)
    );
    
    // Update allSubItemsSelected state only if it changed
    if (allSelected !== allSubItemsSelected) {
      setAllSubItemsSelected(allSelected);
    }
    
    // Log for debugging
    console.log('Current selected options:', localAccessibilityOptions);
    console.log('All sub-items selected:', allSelected);
  }, [localAccessibilityOptions, accessibilityFeaturesRequirementsHiddenIds]);

  // Helper function to ensure unique values
  const updateAccessibilityOptions = (newOptions: number[]) => {
    // Ensure we only have unique values
    const uniqueOptions = [...new Set(newOptions)];
    console.log('Setting new options:', uniqueOptions);
    setLocalAccessibilityOptions(uniqueOptions);
  };

  const handleSubOptionClick = (tagId: number, isChecked: boolean) => {
    const currentOptions = [...localAccessibilityOptions];
    
    if (isChecked) {
      // Remove this sub-option
      const newOptions = currentOptions.filter(id => id !== tagId);
      console.log('Removing sub-option:', tagId, 'New options:', newOptions);
      updateAccessibilityOptions(newOptions);
    } else {
      // Add this sub-option if not already present
      if (!currentOptions.includes(tagId)) {
        const newOptions = [...currentOptions, tagId];
        console.log('Adding sub-option:', tagId, 'New options:', newOptions);
        updateAccessibilityOptions(newOptions);
      }
    }
  };

  const handleAllClick = (isChecked: boolean) => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map(id => Number(id));
    const currentOptions = [...localAccessibilityOptions];
    
    if (isChecked) {
      // Uncheck all sub-options but keep parent and other selections
      console.log('Unchecking all sub-options');
      const newOptions = currentOptions.filter(id => 
        !subOptionIds.includes(id) || id === parentId
      );
      console.log('New options after unchecking all:', newOptions);
      updateAccessibilityOptions(newOptions);
    } else {
      // Check all sub-options while preserving other selections
      console.log('Checking all sub-options');
      // First ensure parent is included
      const newOptions = currentOptions.includes(parentId) 
        ? [...currentOptions]
        : [...currentOptions, parentId];
      
      // Then add any missing sub-options
      const optionsWithSubItems = [...newOptions];
      subOptionIds.forEach(id => {
        if (!optionsWithSubItems.includes(id)) {
          optionsWithSubItems.push(id);
        }
      });
      
      console.log('New options after checking all:', optionsWithSubItems);
      updateAccessibilityOptions(optionsWithSubItems);
    }
  };

  const handleClose = () => {
    AnalyticsService.trackGAEventHandler("nearbyVenuesListCloseFilters");
    AnalyticsService.trackMx("nearbyVenuesListCloseFilters", {});
    onClose();
  };

  const handleClearAll = () => {
    AnalyticsService.trackGAEventHandler("nearbyVenuesListClearedFilter");
    AnalyticsService.trackMx("nearbyVenuesListClearedFilter", {});        
    setLocalAccessibilityOptions([]);
    setAllSubItemsSelected(false);
    
    console.log('Clearing all selections');
    setStatusMessage(i18n.get("filtersCleared") || "All filters cleared");
    
    setFiltersState((prev) => {
      return {
        ...prev,
        openingHoursSelected: false,
        accessibilityOptions: [],
        isFiltersApplied: false,
        filtersApplied: null,
        selectedBusinessTypes: [],
      };
    });
    
  };

  const handleSetFilters = () => {
    const types = localBusinessTypes.join("|");
    const accessibilities = localAccessibilityOptions
      ?.map((o) => o.toString())
      .join("|");
    const showVenueOptions = filtersState?.openingHoursSelected
      ? "Open Now"
      : "All";

    console.log('Applying filters with options:', localAccessibilityOptions);

    if (
      types.length <= 0 &&
      accessibilities.length <= 0 &&
      !filtersState?.openingHoursSelected
    ) {
      setFiltersState({
        ...filtersState,
        isFiltersApplied: false,
        filtersApplied: null,
        accessibilityOptions: [] // Explicitly clear
      });
      setStatusMessage(i18n.get("noFiltersApplied") || "No filters applied");
    } else {
      AnalyticsService.trackGAEventHandler("nearbyVenuesListAppliedFilters");
      AnalyticsService.trackMx("nearbyVenuesListAppliedFilters", {
        FilterTags: types,
      });

      AnalyticsService.trackGAEventHandler("nearbyVenuesListAppliedFilters");
      AnalyticsService.trackMx("nearbyVenuesListAppliedFilters", {
        FilterTags: accessibilities,
      });

      setFiltersState({
        ...filtersState,
        isFiltersApplied: true,
        filtersApplied: { types, accessibilities, showVenueOptions },
        selectedBusinessTypes: localBusinessTypes,
        accessibilityOptions: localAccessibilityOptions // Explicitly include
      });
      setStatusMessage(i18n.get("filtersApplied") || "Filters applied");
    }
    
    handleClose();
  };

  const isSelected = (id: number) => {
    return localAccessibilityOptions?.includes(id) || false;
  };

  const firstFocusFilterRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (open && firstFocusFilterRef.current) {
      console.log("open", open);
      // Delay focusing to ensure the element is rendered
      setTimeout(() => {
        console.log("drawerMenuOpen focus set");

        firstFocusFilterRef.current.focus();
      }, 1000);
    }
  }, [open]); // Trigger the effect when the drawer menu opens

  const setSelectedOptions = (options: number) => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map(id => Number(id));
    const currentOptions = [...localAccessibilityOptions];
    
    if (!currentOptions.includes(options)) {
      console.log('Adding option:', options);
      
      // If this is the parent option being checked, add all sub-options as well
      if (options === parentId) {
        console.log('Parent option checked, adding all sub-options');
        const newOptions = [...currentOptions, options, ...subOptionIds];
        updateAccessibilityOptions(newOptions);
        setAllSubItemsSelected(true);
      } else {
        // Just add this specific option
        updateAccessibilityOptions([...currentOptions, options]);
      }
    } else {
      console.log('Removing option:', options);
      
      if (options === parentId) {
        // When unchecking parent, remove all sub-options
        setAllSubItemsSelected(false);
        const tagsIdsSet = new Set(subOptionIds);
        const newOptions = currentOptions.filter(
          (option) => !tagsIdsSet.has(option) && option !== options
        );
        updateAccessibilityOptions(newOptions);
      } else {
        // Just remove this specific option
        const newOptions = currentOptions.filter(o => o !== options);
        updateAccessibilityOptions(newOptions);
      }
    }
  };

  const isAnyOneSelected = (tags: any[]) => {
    const currentIds = tags.map((o) => o.id);
    return localAccessibilityOptions.some((id) =>
      currentIds.includes(id)
    );
  };

  const toggleCategory = (categoryId: number) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleShowAllTags = (categoryId: number) => {
    setShowAllTags(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  return (
    <Sheet open={open} onOpenChange={() => handleClose()}>
      <SheetContent
        showClose={false}
        side="left"
        className="w-full bg-white dark:bg-primary-900 nearbyFilterSheet"
      >
        <div className="flex flex-col h-full">
          {/* Add a visually hidden live region to announce filter status changes */}
          <div 
            aria-live="polite" 
            className="sr-only" 
            role="status"
          >
            {statusMessage}
          </div>
          <button
            onClick={() => handleClose()}
            className="flex flex-row items-center"
            aria-label={`Back to ${i18n.get("listview")}`}
          >
            <IoArrowBackOutline size={22} />
            <div className="ml-2 button-small-semi-bold text-primary-800 dark:text-primary-150">
              {i18n.get("listview")}
            </div>
          </button>
          <section className="mt-[20px]">
            <h1
              className="text-primary-800 dark:text-primary-150 desktop-h4-medium-semi-bold mb-4"
              tabIndex={0}
            >
              <span
                tabIndex={-1}
                ref={firstFocusFilterRef} // Attach the ref here
              >
                {i18n.get("filters")}
              </span>
            </h1>
          </section>
        
          <div className="overflow-y-scroll scroll-m-0 mb-10">
            <section className="">
              {nearbyFilterAccessibility?.filter(category => category.accessibility_tags?.length > 0).map((category) => {
                // Filter out tags that are in accessibilityFeaturesRequirementsHiddenIds
                const filteredTags = category.accessibility_tags.filter(tag => 
                  !accessibilityFeaturesRequirementsHiddenIds.some(id => Number(id) === tag.id)
                );
                
                const actualTagsToShow = showAllTags[category.id] 
                  ? filteredTags 
                  : filteredTags.slice(0, 5);

                return (
                  <div key={category.id}>
                    <header
                      id={`category-header-${category.id}`}
                      tabIndex={0}
                      role="button"
                      onClick={() => toggleCategory(category.id)}
                      aria-expanded={!!openCategories[category.id]}
                      aria-controls={`category-panel-${category.id}`}
                      className={`p-4 flex items-center duration-200 ${
                        !isAnyOneSelected(filteredTags) ? "bg-primary-100" : "bg-primary-200"
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-grow">
                        {category.image && (
                          <img
                            src={bucketUrlPrefix + (isDark ? category.image_dark || category.image : category.image)}
                            alt=""
                            className="w-6 h-6"
                            aria-hidden="true"
                          />
                        )}
                        <h2 className="text-primary-900 desktop-h3-small-semi-bold">
                          {category.name}
                        </h2>
                      </div>
                      <img
                        className={`duration-200 ${openCategories[category.id] ? "-rotate-180" : ""}`}
                        src="/images/CaretDown.svg"
                        alt=""
                        aria-hidden="true"
                      />
                    </header>
                    <ReactSmoothCollapse expanded={openCategories[category.id]}>
                      <ul 
                        id={`category-panel-${category.id}`} 
                        className="p-4 flex flex-col gap-4" 
                        role="region" 
                        aria-labelledby={`category-header-${category.id}`}
                      >
                        {actualTagsToShow.map((tag) => {
                          // Skip rendering if tag id is in accessibilityFeaturesRequirementsHiddenIds
                          if (accessibilityFeaturesRequirementsHiddenIds.some(id => Number(id) === tag.id)) {
                            return null;
                          }
                          return (
                            <div key={tag.id}>
                              <Checkbox
                                checked={isSelected(tag.id)}
                                onCheck={() => setSelectedOptions(tag.id)}
                                label={tag.name}
                                aria-label={`${tag.name}, ${isSelected(tag.id) ? "selected" : "not selected"}`}
                              />
                              {tag.id === Number(accessibilityFeaturesRequirementsShowHiddenById) && localAccessibilityOptions.includes(tag.id) && (
                                <div key={tag.id + "321"}>
                                  <p className="mt-2 ml-6" tabIndex={0}>
                                    {i18n.get("wouldAnyOfTheseAlsoWork")}
                                  </p>
                                  <Checkbox
                                    className="mt-2 ml-6"
                                    key="all"
                                    label="All"
                                    checked={allSubItemsSelected}
                                    onCheck={() => handleAllClick(allSubItemsSelected)}
                                    aria-label={`Select all options, ${allSubItemsSelected ? "currently selected" : "currently not selected"}`}
                                  />
                                  {accessibilityFeaturesRequirementsHiddenIds.map((tagId) => {
                                    // Find the tag object that matches this ID
                                    const matchingTag = category.accessibility_tags.find(tag => tag.id === Number(tagId));
                                    const numTagId = Number(tagId);
                                    const isChecked = localAccessibilityOptions.includes(numTagId);
                                    
                                    return (
                                      <Checkbox
                                        className="mt-2 ml-12"
                                        key={tagId}
                                        label={matchingTag ? matchingTag.name : tagId.toString()}
                                        checked={isChecked}
                                        onCheck={() => handleSubOptionClick(numTagId, isChecked)}
                                        aria-label={`${matchingTag ? matchingTag.name : tagId.toString()}, ${isChecked ? "selected" : "not selected"}`}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </ul>
                      {category.accessibility_tags.length > 5 && (
                        <button
                          tabIndex={0}
                          onClick={() => toggleShowAllTags(category.id)}
                          className="ml-4 mb-4 text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                          aria-expanded={!!showAllTags[category.id]}
                          aria-controls={`category-panel-${category.id}`}
                        >
                          {!showAllTags[category.id] ? i18n.get("showMore") : i18n.get("showLess")}
                        </button>
                      )}
                    </ReactSmoothCollapse>
                  </div>
                );
              })}
            </section>
          </div>
          <section className="absolute left-0 right-0 bottom-0 flex flex-row justify-between items-center px-[20px] bg-white dark:bg-primary-900 py-4">
            <div
              role="button"
              className="text-links dark:text-primary-200 button-regular-semi-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={handleClearAll}
              tabIndex={0}
            >
              {i18n.get("clearAll")}
            </div>
            <button
              onClick={handleSetFilters}
              className="px-6 py-3 rounded-xl text-white dark:text-primary-800 bg-primary-800 dark:bg-primary-200 button-regular-semi-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
              tabIndex={0}
            >
              {i18n.get("applyFilters")}
            </button>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default NearbyFiltersSheet;
